import React from "react";
import styled from "styled-components";
import { RadioOption } from "../../atoms/RadioOption";
import { DateField } from "../DateField";
import { NumberStepper } from "../NumberStepper";
import { spacing } from "../../../design-system/tokens/spacing";
import type { EndsMode } from "../../../utils/storyRefreshTypes";
import {
  Field,
  FieldLabel,
  HelperText,
} from "../shared/scheduleField.styles";

export interface ScheduleEndsFieldProps {
  mode: EndsMode;
  /** ISO yyyy-mm-dd — the "On" date. */
  endsOn: string;
  /** Occurrence count — the "After" value. */
  endsAfter: number | null;
  /** Earliest selectable end date (the schedule's own start). */
  minDate?: string;
  onChangeMode: (mode: EndsMode) => void;
  onChangeEndsOn: (iso: string) => void;
  onChangeEndsAfter: (n: number) => void;
  endsOnError?: string;
  endsAfterError?: string;
  showError?: boolean;
  showAfter?: boolean;
  className?: string;
}

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  width: 100%;
`;

/** Fixed label column so the three controls line up under each other. */
const RadioSlot = styled.div`
  width: ${spacing[36]};
  flex: 0 0 auto;
`;

/**
 * Inactive controls stay in place with their own muted text/icon styling.
 */
const ControlSlot = styled.div<{ $enabled: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  pointer-events: ${({ $enabled }) => ($enabled ? "auto" : "none")};
`;

const RADIO_GROUP = "story-refresh-ends";

/**
 * "Ends" — Never / On <date> / After <n> occurrences.
 *
 * The disabled controls stay mounted and `aria-hidden`-free but are
 * pointer-inert, matching Figma's dimmed-in-place treatment. Validation for a
 * dimmed row never fires, because `validateStoryRefresh` only checks the
 * branch that `mode` actually selects.
 */
export const ScheduleEndsField: React.FC<ScheduleEndsFieldProps> = ({
  mode,
  endsOn,
  endsAfter,
  minDate,
  onChangeMode,
  onChangeEndsOn,
  onChangeEndsAfter,
  endsOnError,
  endsAfterError,
  showError = false,
  showAfter = true,
  className,
}) => {
  const visibleOnError = showError && endsOnError ? endsOnError : undefined;
  const visibleAfterError =
    showError && endsAfterError ? endsAfterError : undefined;

  return (
    <Field className={className}>
      <FieldLabel id="story-refresh-ends-label">Ends</FieldLabel>
      <Rows role="radiogroup" aria-labelledby="story-refresh-ends-label">
        <Row>
          <RadioOption
            name={RADIO_GROUP}
            value="never"
            checked={mode === "never"}
            label="Never"
            onChange={() => onChangeMode("never")}
          />
        </Row>

        <Row>
          <RadioSlot>
            <RadioOption
              name={RADIO_GROUP}
              value="on"
              checked={mode === "on"}
              label="On"
              onChange={() => onChangeMode("on")}
            />
          </RadioSlot>
          <ControlSlot $enabled={mode === "on"}>
            <DateField
              value={endsOn}
              min={minDate}
              placeholder="Pick an end date"
              invalid={Boolean(visibleOnError)}
              ariaLabel="Schedule end date"
              iconLayout="calendar-end"
              muted={mode !== "on"}
              onChange={onChangeEndsOn}
            />
          </ControlSlot>
        </Row>
        {visibleOnError && <HelperText $error>{visibleOnError}</HelperText>}

        {showAfter && (
          <>
            <Row>
              <RadioSlot>
                <RadioOption
                  name={RADIO_GROUP}
                  value="after"
                  checked={mode === "after"}
                  label="After"
                  onChange={() => onChangeMode("after")}
                />
              </RadioSlot>
              <ControlSlot $enabled={mode === "after"}>
                {/* Unit word sits inside the control, per Figma "12 occurrences". */}
                <NumberStepper
                  value={endsAfter ?? 1}
                  min={1}
                  max={999}
                  invalid={Boolean(visibleAfterError)}
                  ariaLabel="Number of occurrences"
                  muted={mode !== "after"}
                  suffix={
                    (endsAfter ?? 1) === 1 ? "occurrence" : "occurrences"
                  }
                  compactSuffix
                  onChange={onChangeEndsAfter}
                />
              </ControlSlot>
            </Row>
            {visibleAfterError && (
              <HelperText $error>{visibleAfterError}</HelperText>
            )}
          </>
        )}
      </Rows>
    </Field>
  );
};

export default ScheduleEndsField;
